import MainComponent from "components/MainComponent";
import React, { useState, useCallback } from "react";
import * as XLSX from "xlsx";
import { formatDataStateCountyExcelData } from "utils/utils";
import EconomyNexus from "./EconomyNexus";
import UploadModal from "./components/sections/UploadModal";
import StateSelectionSection from "./components/sections/StateSelectionSection";
import StateTaxRatesSection from "./components/sections/StateTaxRatesSection";
import CountyManagementSection from "./components/sections/CountyManagementSection";
import styles from "./Tax.module.scss";
import { templateData } from "./helper/constant";
import {
  useTaxData,
  useUpdateState,
  useUploadZipCodes,
} from "./hooks/useTaxQueries";

const Tax = () => {
  const [taxState, setTaxState] = useState();
  const [showSheetModal, setShowSheetModal] = useState(false);
  const [sheetUploadData, setSheetUploadData] = useState([]);

  const { data: taxData, isLoading } = useTaxData(taxState);
  const updateStateMutation = useUpdateState();
  const uploadZipCodesMutation = useUploadZipCodes();

  const currentState = taxData;
  const stateAndCounties = taxData?.counties || [];

  const onStateChange = useCallback((e) => {
    setTaxState(e.target.value);
  }, []);

  const onActiveChange = useCallback(
    (e) => {
      const newActive = e.target.value === "active";
      if (currentState?._id) {
        updateStateMutation.mutate({ id: currentState._id, active: newActive });
      } else if (taxState) {
        updateStateMutation.mutate({ stateName: taxState, active: newActive });
      }
    },
    [currentState, taxState, updateStateMutation],
  );

  const handleFileUpload = useCallback((e) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet);
      const formatData = formatDataStateCountyExcelData(jsonData);
      setSheetUploadData(formatData);
      setShowSheetModal(true);
    };

    reader.readAsArrayBuffer(file);
  }, []);

  const uploadSheetDataToBackend = useCallback(() => {
    uploadZipCodesMutation.mutate(
      { typeOfUpload: "sheet", sheetData: sheetUploadData },
      { onSuccess: () => setShowSheetModal(false) },
    );
  }, [sheetUploadData, uploadZipCodesMutation]);

  const downloadTemplate = useCallback(() => {
    const ws = XLSX.utils.json_to_sheet(templateData);
    ws["!cols"] = [
      { wch: 20 },
      { wch: 15 },
      { wch: 12 },
      { wch: 20 },
      { wch: 10 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "zip_codes_template.xlsx");
  }, []);

  const toggle = useCallback(() => {
    setShowSheetModal(!showSheetModal);
  }, [showSheetModal]);

  return (
    <MainComponent>
      <div className={styles.taxContainer}>
        <StateSelectionSection
          taxState={taxState}
          onStateChange={onStateChange}
          activeValue={currentState?.active ? "active" : "in-active"}
          onActiveChange={onActiveChange}
          isLoading={isLoading}
          styles={styles}
        />

        <div className={styles.divider} />

        {taxState && (
          <div key={taxState}>
            <StateTaxRatesSection
              taxState={taxState}
              isLoading={isLoading}
              styles={styles}
            />

            <div className={styles.divider} />

            <CountyManagementSection
              taxState={taxState}
              onDownloadTemplate={downloadTemplate}
              onFileUpload={handleFileUpload}
              isLoading={isLoading}
              styles={styles}
            />

            <EconomyNexus />
          </div>
        )}
      </div>

      <UploadModal
        isOpen={showSheetModal}
        toggle={toggle}
        sheetUploadData={sheetUploadData}
        isLoading={isLoading || uploadZipCodesMutation.isPending}
        onUpload={uploadSheetDataToBackend}
        styles={styles}
      />
    </MainComponent>
  );
};

export default Tax;
